import { useSyncExternalStore } from 'react';
import { domainEventBus } from '~/domain/events';
import {
    WindowLevelDeltaEvent,
    WindowLevelSetEvent,
} from '~/domain/events/WindowingDomainEvents';
import { Image3D } from '~/domain/patient/image3d';
import { WindowLevel } from '~/domain/patient/windowLevel';

/**
 * Hook that subscribes to window/level changes via the domain event bus.
 * Uses useSyncExternalStore to efficiently bridge external state with React.
 *
 * @param image - The image to get window level from
 * @returns The current WindowLevel, or undefined if no image
 */
export function useWindowLevelSubscription(
    image: Image3D | undefined
): WindowLevel | undefined {
    const seriesUid = image?.seriesUid;

    return useSyncExternalStore(
        (onStoreChange) => {
            if (!seriesUid) {
                return () => {};
            }

            const deltaSubscription = domainEventBus.subscribe(
                WindowLevelDeltaEvent.EVENT_TYPE,
                (event: WindowLevelDeltaEvent) => {
                    if (event.seriesUid === seriesUid) {
                        onStoreChange();
                    }
                }
            );

            const setSubscription = domainEventBus.subscribe(
                WindowLevelSetEvent.EVENT_TYPE,
                (event: WindowLevelSetEvent) => {
                    if (event.seriesUid === seriesUid) {
                        onStoreChange();
                    }
                }
            );

            return () => {
                deltaSubscription.unsubscribe();
                setSubscription.unsubscribe();
            };
        },
        () => image?.windowLevel,
        () => undefined
    );
}
